/**
 * Enhanced Shared Cart Service
 * 
 * Improved version with better error handling, performance optimizations
 * 
 * ## ✅ Key Features:
    * Save full cart data from local storage to shop metafield (`userNumber_cart_data`)
    * Retrieve full cart data from the shop metafield  which is saved in local storage.
    * Call the API to fetch the latest, user-specific variant prices from the variant metafields.
    * Save the updated JSON (with the latest prices) back into local storage and metafield cart data.
    * 
    * **Checkout lock:
    * If `is_checkout_started` is `true`, rebuild is skipped.
    * Lock message is shown.
    * Prevents overwriting the cart mid-checkout.
    * 
    * **Sync runs:
    * 
    * On `cart update` (origin device → save in local storage → save to metafield).
    * On `login` (other devices → rebuild from metafield JSON data save in local storage).
    * On `page load` (if logged in).
    * On `checkout start` (sets lock in metafield).
    * On `checkout finished` (clear shop metafiled and local storage).
    * 
    * NOTE:
    * handleCartChange(); method call to save cart to metafield.
 * @author Dynamic Dreamz Team
 * @version 1.1
 */


class SharedCart {
  constructor() {
    this.localKey = `shared_cart_json`;
    this.localCart = `shopify_local_cart`;
    this.shopApiUrl = `/apps/stroetmann/api/v1/users/set_user_cart`;
    this.getVariantPriceUrl = `/apps/stroetmann/api/v1/users/get_variant_price`;
    this.sc_timestamp = Date.now();
    this.currentToken = localStorage.getItem(`current_cart_token`);
  }

  async updateShopifyCartLockAttr() {

    console.log('✅ Cleared All Checkout Flags & Related Local Storage');
    localStorage.removeItem('checkoutId');
    localStorage.removeItem('checkout_progress');
    localStorage.removeItem('checkout_timeout');
    localStorage.removeItem('checkout_update_checkout_timeout');
    localStorage.removeItem('checkout_protection_active');
    localStorage.removeItem('checkout_started');
    localStorage.removeItem('checkout_lock_timestamp');
    localStorage.removeItem('current_step');

    const formData = new FormData();
    formData.set('attributes[locked]', '');
    formData.set('attributes[bonus_amount]', '');
    formData.set('attributes[bonus_amount_max]', '');
    formData.set('attributes[current_date]', '');

    await fetch(`${window.theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: {
        'Bypass-Internal': 'true',
        'No-Queue': 'true'
      },
      body: formData,
    });
  }

  /** Push cart JSON to backend metafield */
  async saveCartToMetafield(cart) {

    if (!cart) return false;
    if (window.customer?.currentUserNumber == '' || !window.customer?.currentUserNumber) return false;

    // 🔒 Prevent sync if checkout is started from other device
    // if (cart.is_checkout_started && cart.token !== this.currentToken) {
    //   console.log('🔒 Checkout in progress from other device → skipping sync to metafileds');
    //   return;
    // }

    // Compare current user number then save to metafield if matched
    if (window.customer?.currentUserNumber !== cart.userNumber) {
      console.log('❌ currentUserNumber not matched → no save needed to metafield at this time');
      return false;
    }

    //save latest timestamp to cart
    cart.timestamp = Date.now();

    try {
      //console.log(`save cart to metafiled >>`, cart);
      const response = await fetch(this.shopApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cart),
      });

      if (!response.ok) {
        console.error('❌ Error saving cart to metafield:');
        return false;
      }
      console.log('✅ Local cart saved to metafield >>', cart);
      return true;
    } catch (err) {
      console.error('❌ Error saving cart to metafield:');
    }
  }

  /** Fetch the latest, user-specific variant prices from the variant metafields */
  async fetchLatestPricesFromMeta(cart) {
    if (!cart) return;

    const itemIds = cart?.items?.map(item => item.id) ?? [];

    if (!itemIds.length) return;

    const payload = {
      "customerId": `${window.customer?.id}`,
      "userName": `${window.customer?.userName}`,
      "userNumber": `${window.customer?.currentUserNumber}`,
      "currentUser": `${window.customer?.currentUserNumber}`,
      "productVariantIds": itemIds,
      "deliveryDate": cart?.attributes?.current_date || new Date().toISOString()
    };

    if (!payload.currentUser) return;

    try {
      const response = await fetch(this.getVariantPriceUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('⏳ Fetch latest prices from variant metafield');

      if (data?.price?.length) {
        const priceMap = Object.fromEntries(data.price.map(p => [p.id, p.price]));

        let isUpdated = false;
        cart.items.map(item => {
          const newPrice = (priceMap[item.id] * 100).toFixed(0);
          if (newPrice && item.properties?._price != newPrice) {
            isUpdated = true;
            const props = {
              ...item.properties,
              _price: (newPrice * 1)
            };
            LocalCart.updateProps(item.id, props);
            LocalCart.updateQuantity(item.id, item.quantity); // this is due to update the line prices and final line prices purpose call;
          }
        });

        // Only update localStorage if something actually changed
        if (isUpdated) {
          // saved updated cart to metafield
          await this.saveCartToMetafield(LocalCart.getState());
          console.log('✅ Local cart updated with latest prices');
          /**
           * Refresh cart items section
          */
          setTimeout(() => {
            if (window.location.pathname.includes(`/cart`)) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                cartItems.refreshCartItems();
              }
            }
          }, 200);
        } else {
          console.log('ℹ️ No price changes detected, local cart as is');
        }
      }

    } catch (err) {
      console.error('❌ Error fetching latest prices from variant metafield:', err);
    }
  }

  /** Sync cart from localStorage metafield JSON */
  async syncFromLocalStorage() {

    const currentCart = LocalCart.getState();
    if (!currentCart) return;

    // clear checkout flag attr
    if (currentCart.is_clear_checkout_flag) {
      console.log('🔄 Clearing Checkout flags (LOCAL CART  ATTRIBUTES) >>');
      await this.clearCartAttrAndFlag(currentCart);
    }

    await this.clearCheckoutStateAfterCertainTime();

    await this.fetchLatestPricesFromMeta(currentCart);
  }

  async clearCartAttrAndFlag(cart) {
    if (!cart) return;
    try {
      cart.is_clear_checkout_flag = false;
      cart.is_checkout_started = false;
      delete cart.attributes.locked; // deleted locked attr
      delete cart.attributes.bonus_amount; // deleted bonus attr
      delete cart.attributes.bonus_amount_max; // deleted bonus max attr
      delete cart.attributes.current_date; // delet stale (old) date
      LocalCart.save(cart);

      // Clear all checkout-related localStorage items and shopify cart attributes
      await this.updateShopifyCartLockAttr();

      // saved updated local cart to metafield
      const saveToMetafield = await this.saveCartToMetafield(LocalCart.getState());
      let saveRetryCount = 0;
      if (saveToMetafield) {
        setTimeout(() => {
          window.location.reload();
        }, 225);
      } else {
        // retry once
        if (saveRetryCount < 1) {
          saveRetryCount += 1;
          const result = await this.saveCartToMetafield(LocalCart.getState());
          setTimeout(() => {
            window.location.reload();
          }, 225);
        }
      }
    } catch (e) {
      console.error(`ℹ️ Unable to clear checkout flags >>`, e);
    }
  }

  /** Clear checkout flag after 20 min from checkout started time */
  /** Back-end also cleares the checkout flag after 20 min from checkout started time but this is for safty nets if back-end fails to clear */
  async clearCheckoutStateAfterCertainTime() {
    try {
      const cart = LocalCart.getState();

      // Safety checks
      if (!cart?.timestamp) return;

      // only clears the falg if it is active
      if (cart.is_checkout_started === true && cart.is_clear_checkout_flag === false) {

        const CHECKOUT_TIMEOUT = 20 * 60; // 20 minutes in seconds

        // Convert timestamps
        const cartTimestamp = parseInt(cart.timestamp, 10); // already in seconds
        const currentTimestamp = Math.floor(Date.now() / 1000); // convert ms → seconds

        const timeDiff = currentTimestamp - cartTimestamp;

        // If timeout exceeded → clear flags
        if (timeDiff >= CHECKOUT_TIMEOUT) {
          await this.clearCartAttrAndFlag(cart);
          console.log('Checkout state cleared successfull after timeout');
        }
      }
    } catch (error) {
      console.error('Error clearing checkout state:', error);
    }
  }

  /** Handle local cart change */
  async handleCartChange() {
    let cart = LocalCart.getState();
    if (!cart) return false;

    const saveToMetafield = await this.saveCartToMetafield(cart); // also save to backend
    //save cart items text to below variable
    const globalCartItemsVariable = document.querySelector(`[data-cart-items]`);
    if (globalCartItemsVariable) globalCartItemsVariable.innerText = JSON.stringify(cart.items);
    return saveToMetafield;
  }

  /* Save updated cart data attr */
  //if customer not logged in
  async saveCartDataToVariable() {
    const cart = LocalCart.getState();
    //save cart items text to below variable
    const globalCartItemsVariable = document.querySelector(`[data-cart-items]`);
    if (globalCartItemsVariable) globalCartItemsVariable.innerText = JSON.stringify(cart.items);
  }

  isValidObject(obj) {
    return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
  }


  /** Init listeners */
  init() {

    if (window.customer) {
      // check once on page load (for logged-in customers)
      this.syncFromLocalStorage();

      // Listen for cart updates event
      document.addEventListener('cart:updated', async (event) => {
        console.log('✅ cart:updated event fired', event.detail);
        const isUpdateSuccess = await this.handleCartChange();
        const detail = event?.detail;
        const source = detail?.source;
        const item = detail?.item;
        const qtyEle = document.querySelector(`quickview-modal[open] quantity-input[data-product-id="${item?.productId}"]`) || document.querySelector(`quantity-input[data-product-id="${item?.productId}"]`);
        const qtyEleAll = document.querySelectorAll(`quantity-input[data-product-id="${item?.productId}"]`);
        if (isUpdateSuccess) {
          console.log('✅ updated cart save to metafield...');
          // remove error element if have
          if (qtyEle.closest(`product-form`).querySelector(`.atc-error`)) {
            qtyEle.querySelector(`.atc-error`).remove();
          }
          //Update the quantity-input value for this variant inside a kios cached
          // if (AssortmentCache) await AssortmentCache.patchCartQty(String(event?.detail?.item?.productId), String(event?.detail?.item?.variantId), Number(event?.detail?.item?.quantity), event?.detail?.item?.key).catch(() => {});
          //refresh the assortment page if is open
          localStorage.setItem('tab_refresh_trigger', Date.now().toString());
        } else {
          console.log('❌ updated cart does not save to metafield...');
          if (source == 'quantity-item') {
            if (qtyEle) {
              // add error element
              if (!qtyEle.closest(`product-form`).querySelector(`.atc-error`)) {
                qtyEle.insertAdjacentHTML("afterend", '<div class="text-center text-sm color-red atc-error"><span>Add to cart failed, try again</span></div>');
              }
              //update inputed value
              const inputEle = qtyEle.querySelector(`input`);
              if (inputEle) {
                if (detail?.operation == 'add') {
                  LocalCart.updateQuantity(item?.variantId, 0);
                  if (qtyEleAll.length) {
                    qtyEleAll.forEach(ele => {
                      ele.querySelector(`input`).value = ``;
                      ele.classList.remove(`in-cart`);
                      ele.dataset.lineItem = ``;
                    });
                  }
                } else {
                  let originalQty = parseInt(item?.before_change_qty) || 0;
                  LocalCart.updateQuantity(item?.variantId, originalQty);
                  if (qtyEleAll.length) {
                    qtyEleAll.forEach(ele => {
                      ele.querySelector(`input`).value = originalQty > 0 ? originalQty : ``;
                      if (originalQty <= 0) {
                        ele.classList.remove(`in-cart`);
                        ele.dataset.lineItem = ``;
                      }
                    });
                  }
                }
              }
            }
          }
        }
      });

    } else {
      // Listen for cart updates event for logged out customer
      document.addEventListener('cart:updated', async (event) => {
        console.log('✅ cart:updated event fired');
        this.saveCartDataToVariable();
      });
    }
  }
}

const sharedCart = new SharedCart();
sharedCart.init();
